import { Download, MessageCircle, Share2, Home } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { CONSULTANT_NAME } from '@/constants';
import { toast } from 'sonner';

export default function PhotoResult({ photoUrl, photoId, userName, onReset }) {
  const fullPhotoUrl = photoUrl;
  const absolutePhotoUrl =
    typeof window !== 'undefined' && photoUrl.startsWith('/')
      ? `${window.location.origin}${photoUrl}`
      : photoUrl;

  const shareText = `Veja a minha foto no kart! Cortesia Century 21 - ${CONSULTANT_NAME}`;

  const handleDownload = async () => {
    try {
      const response = await fetch(fullPhotoUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `minha-foto-kart-c21-${photoId}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Foto guardada!');
    } catch {
      toast.error('Erro ao guardar. Tente novamente.');
    }
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + absolutePhotoUrl)}`;
    window.open(url, '_blank');
  };

  const handleFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(absolutePhotoUrl)}`;
    window.open(url, '_blank');
  };

  const handleInstagram = async () => {
    if (typeof navigator !== 'undefined' && navigator.canShare) {
      try {
        const response = await fetch(fullPhotoUrl);
        const blob = await response.blob();
        const file = new File([blob], `foto-kart-c21.jpg`, { type: 'image/jpeg' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${userName} no Kart - Century 21`,
            text: shareText,
            files: [file],
          });
          return;
        }
      } catch {
        return;
      }
    }
    handleDownload();
    toast.success('Foto guardada! Partilhe nos seus Stories.');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        const response = await fetch(fullPhotoUrl);
        const blob = await response.blob();
        const file = new File([blob], `foto-kart-c21.jpg`, { type: 'image/jpeg' });
        await navigator.share({
          title: `${userName} no Kart - Century 21`,
          text: shareText,
          files: [file],
        });
      } catch {
        // User cancelled or error
      }
    }
  };

  return (
    <div className="px-6 py-8 animate-fade-in" data-testid="photo-result-screen">
      <h2 className="font-headline text-xl mb-4 text-white text-center">
        A sua foto no kart!
      </h2>

      {/* Generated Image */}
      <div
        className="rounded-xl overflow-hidden mb-6 mx-auto"
        style={{
          border: '2px solid rgba(181,152,90,0.4)',
          maxWidth: '360px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
      >
        <img
          src={fullPhotoUrl}
          alt={`${userName} no kart gerada com IA`}
          className="w-full"
          data-testid="photo-result-image"
          loading="eager"
        />
      </div>

      <p
        className="font-body text-center mb-6"
        style={{ fontSize: '0.8rem', color: '#B5985A' }}
      >
        Foto gerada com IA por cortesia de {CONSULTANT_NAME} — Century 21
      </p>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3 mb-4" data-testid="photo-result-actions">
        <button
          onClick={handleDownload}
          className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl transition-colors"
          style={{
            background: '#1A1A1A',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
          data-testid="photo-result-download-btn"
        >
          <Download size={22} strokeWidth={1.5} style={{ color: '#B5985A' }} />
          <span className="font-cta text-xs text-white">Guardar</span>
        </button>

        <button
          onClick={handleWhatsApp}
          className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl transition-colors"
          style={{
            background: '#1A1A1A',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
          data-testid="photo-result-whatsapp-btn"
        >
          <MessageCircle size={22} strokeWidth={1.5} style={{ color: '#25D366' }} />
          <span className="font-cta text-xs text-white">WhatsApp</span>
        </button>

        <button
          onClick={handleFacebook}
          className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl transition-colors"
          style={{
            background: '#1A1A1A',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
          data-testid="photo-result-facebook-btn"
        >
          <FontAwesomeIcon icon={faFacebookF} style={{ color: '#1877F2', fontSize: 22 }} />
          <span className="font-cta text-xs text-white">Facebook</span>
        </button>

        <button
          onClick={handleInstagram}
          className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl transition-colors"
          style={{
            background: '#1A1A1A',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
          data-testid="photo-result-instagram-btn"
        >
          <FontAwesomeIcon icon={faInstagram} style={{ color: '#E1306C', fontSize: 22 }} />
          <span className="font-cta text-xs text-white">Instagram</span>
        </button>
      </div>

      {/* Native Share (if available) */}
      {typeof navigator !== 'undefined' && navigator.share && (
        <button
          onClick={handleNativeShare}
          className="btn-gold w-full mb-4"
          data-testid="photo-result-native-share-btn"
        >
          <Share2 size={18} strokeWidth={1.5} />
          Partilhar
        </button>
      )}

      {/* Back */}
      <button
        onClick={onReset}
        className="btn-outline w-full"
        data-testid="photo-result-back-btn"
      >
        <Home size={18} strokeWidth={1.5} />
        Voltar ao início
      </button>
    </div>
  );
}
